import { useMemo } from 'react';

import { Box, Icons } from '@cast/design-system';
import { useCurrentOrganizationId } from '@cast/infrastructure-organization';

import { buildDboRoute, DboRoute } from 'features/db-optimization/v2/routes';
import { useDboUrlParams } from 'features/db-optimization/v2/hooks/useDboParams';
import { useDboSidebarV2 } from 'features/db-optimization/v2/hooks/useDboSidebar';

import {
  getFirstVisibleChild,
  isDboParentRoute,
  isParentRouteActive,
  useDboPathActive,
  useVisibleDboNavItems,
} from 'pages/db-optimization/v2/DbOptimizationControllerV2/utils';
import type { DboNavItem } from 'pages/db-optimization/v2/DbOptimizationControllerV2/types';

import { SidebarDivider } from '../SidebarDivider';
import { SidebarItem } from '../SidebarItem';
import { SidebarSection } from '../SidebarSection';
import { sidebarItemListSx } from '../sidebarStyles';
import { DboDatabaseNavItems } from './_components/DboDatabaseNavItems';

const DBO_OVERVIEW_URL = buildDboRoute(DboRoute.OVERVIEW);

export const DboSidebar = () => {
  const organizationId = useCurrentOrganizationId();
  const { dbServerId } = useDboUrlParams();
  const isPathActive = useDboPathActive();

  const { activeServer, isOptimized, loadingServers } = useDboSidebarV2();

  const serverParams = useMemo(
    () => (dbServerId ? { dbServerId } : null),
    [dbServerId]
  );

  const visibleServerItems = useVisibleDboNavItems('server', {
    isOptimized,
    isManualProvider: activeServer?.provider === 'MANUAL',
  });

  return (
    <Box sx={sidebarItemListSx}>
      <SidebarItem
        title="Back to all instances"
        to={DBO_OVERVIEW_URL}
        icon={Icons.ArrowLeft}
      />
      <SidebarDivider />
      <SidebarSection>Instance</SidebarSection>
      {serverParams ? (
        <Box sx={sidebarItemListSx}>
          {visibleServerItems.map((item: DboNavItem) => {
            if (isDboParentRoute(item)) {
              const firstChild = getFirstVisibleChild(item);
              if (!firstChild) return null;
              return (
                <SidebarItem
                  key={item.title}
                  title={item.title}
                  to={buildDboRoute(firstChild.route, serverParams)}
                  active={isParentRouteActive(item, serverParams, isPathActive)}
                  icon={item.icon}
                />
              );
            }

            const path = buildDboRoute(item.route, serverParams);
            return (
              <SidebarItem
                key={item.route}
                title={item.title}
                to={path}
                active={isPathActive(path)}
                icon={item.icon}
              />
            );
          })}
        </Box>
      ) : null}

      {isOptimized ? (
        <>
          <SidebarDivider />
          <DboDatabaseNavItems
            organizationId={organizationId ?? ''}
            isLoadingInstances={loadingServers}
          />
        </>
      ) : null}
    </Box>
  );
};
